import { useEffect, type ReactNode } from 'react';

const APP_BRIDGE_SRC = 'https://cdn.shopify.com/shopifycloud/app-bridge.js';

/**
 * App Bridge v4 is loaded from index.html (CDN) with shopify-api-key meta
 * baked at build time. This provider only:
 *  - fills meta if missing (local misconfig / stale HTML)
 *  - injects the CDN script if the static tag was stripped
 *
 * Do NOT rely on setting meta after mount — App Bridge reads the key when
 * the script first evaluates, which is before React hydrates.
 */
export function AppBridgeProvider({ children }: { children: ReactNode }) {
  useEffect(() => {
    const apiKey = (import.meta.env.VITE_SHOPIFY_API_KEY || '').trim();

    let meta = document.querySelector(
      'meta[name="shopify-api-key"]',
    ) as HTMLMetaElement | null;
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'shopify-api-key';
      document.head.insertBefore(meta, document.head.firstChild);
    }
    // Only patch empty/placeholder meta (build should already set the real key)
    if (apiKey && (!meta.content || meta.content.includes('%VITE_'))) {
      meta.content = apiKey;
    }

    if (!document.querySelector(`script[src="${APP_BRIDGE_SRC}"]`)) {
      const script = document.createElement('script');
      script.src = APP_BRIDGE_SRC;
      script.async = false;
      document.head.appendChild(script);
    }
  }, []);

  return <>{children}</>;
}
